import { SliceProps } from "@/Slice/index"
import { GRichText } from "@/components"
import { Favorite } from "@/components/Favorite"
import { usePromotionTag } from "@/lib/preFetch/prefetchPromoTag"
import {
    FeaturedValidProduct,
    Link,
    NextImage,
    ProductCarousel,
    ProductsContainer,
    PromotionalImage,
    cn,
    getPdpPrice,
    toFeaturedValidProduct,
    transMoney,
    useFeaturedProducts,
    useI18n,
} from "@jog/react-components"
import { getGtmIdFromCT, imageSizes, typedBoolean } from "@jog/shared"
import { asLink, asText, isFilled } from "@prismicio/helpers"
import { ColorField, ImageField, LinkField, SelectField } from "@prismicio/types"
import { useMemo } from "react"

const Price = ({ isDiscount, price }: { isDiscount: boolean; price: number }) => (
    <div className={cn(isDiscount ? "text-red-main" : "text-black", "font-primary text-13 font-medium leading-4")}>
        {transMoney(price)}
    </div>
)
const Discount = ({ isDiscount, firstPrice }: { isDiscount: boolean; firstPrice: number }) => {
    const { $t } = useI18n()
    if (!isDiscount) return <></>
    return (
        <div className="text-10 text-grey-dark">
            <p>{$t(`Original price: {price}`, { price: transMoney(+firstPrice, { EUR: false }) })}</p>
        </div>
    )
}
const ProductItem = ({
    product,
    className,
    backgroundColor,
    displayNumber,
    lcp,
}: {
    product: FeaturedValidProduct
    backgroundColor?: ColorField
    className?: string
    displayNumber: number
    lcp: boolean
}) => {
    const promotionTag = usePromotionTag(product.promotionalBanner)
    const { currentPrice, originPrice, off } = getPdpPrice(product)
    return (
        <div className={className}>
            <div className="h-full bg-white" style={{ backgroundColor }}>
                <div className="relative inline-block h-full w-full text-gray-base no-underline hover:text-gray-base hover:no-underline">
                    <div className="relative flex h-full flex-col">
                        <div className="group relative">
                            <NextImage
                                alt={product.cover.alt}
                                height={500}
                                width={330}
                                className="img-fluid h-full w-full object-cover"
                                src={product.cover.url}
                                sizes={imageSizes({
                                    lg: { 4: "25vw", 5: "20vw", 6: "16vw" }[displayNumber],
                                    sm: "50vw",
                                })}
                                lcp={lcp}
                            />

                            <PromotionalImage className="absolute top-2.5 right-0 w-17.5" promotionTag={promotionTag} />

                            <Favorite
                                productId={product.id}
                                name={product.name}
                                price={+(product.firstPrice || product.price)}
                                gtmId={getGtmIdFromCT(product.attributes)}
                            />
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col justify-between px-3">
                            <span className="mt-2.75 mb-2 flex-1 text-xs font-medium">{product.name}</span>
                            <span className="flex flex-col gap-1">
                                <Discount isDiscount={off > 0} firstPrice={originPrice} />
                                <Price isDiscount={off > 0} price={currentPrice} />
                            </span>
                        </div>
                    </div>
                    <Link
                        href={product.path}
                        target={product.target ?? "_self"}
                        className="absolute inset-0"
                        aria-label={product.name}
                    />
                </div>
            </div>
        </div>
    )
}

export const useValidProducts = (items: { product_link: LinkField; cover: ImageField }[]) => {
    const { products } = useFeaturedProducts()
    return useMemo<FeaturedValidProduct[]>(
        () =>
            items
                .filter((i) => isFilled.link(i.product_link))
                .map(({ product_link, cover }) => {
                    const product = products.find((product) => asLink(product_link)?.includes(product.path))
                    if (!product) return
                    return toFeaturedValidProduct({ product_link, cover }, product)
                })
                .filter(typedBoolean),
        [items, products]
    )
}

const Carousel = ({
    items,
    primary,
    lcp,
}: {
    items: FeaturedValidProduct[]
    primary: { display_number: SelectField<"4" | "5" | "6">; background_color: ColorField }
    lcp: boolean
}) => (
    <ProductCarousel
        CarouselContainer={({ children, className }) => <div className={cn(className, "-ml-1.5")}>{children}</div>}
        items={items.slice(0, 20)}
        displayNumber={+primary.display_number}
        CarouselItem={({ item, className }) => (
            <ProductItem
                product={item}
                className={cn(className, "pl-1.5")}
                backgroundColor={primary.background_color}
                displayNumber={+primary.display_number}
                lcp={lcp}
            />
        )}
        Button={({ className, ...props }) => <button {...props} className={cn(className, "hover:text-yellow-dark")} />}
    />
)
const gridMap = {
    4: "lg:grid-cols-4",
    5: "lg:grid-cols-5",
    6: "lg:grid-cols-6",
}

export const FeaturedProducts = ({
    slice: { primary, items },
    className,
    lcp,
}: SliceProps<"featured_products"> & { lcp: boolean }) => {
    const validProduct = useValidProducts(items)
    if (!validProduct.length) return <></>
    return (
        <ProductsContainer
            className={className}
            backgroundColor={primary.background_color}
            descriptionColor={primary.description_color}
            titleColor={primary.title_color}
            alignment={primary.alignment}
            title={asText(primary.title)}
            description={primary.description}>
            {primary.carousel ? (
                <Carousel primary={primary} items={validProduct.slice(0, 20)} lcp={lcp} />
            ) : (
                <div className={cn("grid grid-cols-2 gap-3 py-3", gridMap[primary.display_number] || gridMap["6"])}>
                    {validProduct.slice(0, +primary.display_number || 6).map((product, index) => (
                        <ProductItem
                            backgroundColor={primary.background_color}
                            product={product}
                            key={index}
                            lcp={lcp}
                            displayNumber={+primary.display_number}
                        />
                    ))}
                </div>
            )}
        </ProductsContainer>
    )
}

export const CarouselProducts = ({
    slice: { primary, items },
    className,
    lcp,
}: SliceProps<"carousel_products"> & { lcp: boolean }) => {
    const max = +primary.maximum_amount || 20
    const maximumAmount = max < +primary.display_number ? 20 : max

    const validProduct = useValidProducts(items)
    if (!validProduct.length) return <></>
    return (
        <ProductsContainer
            className={className}
            backgroundColor={primary.background_color}
            descriptionColor={primary.description_color}
            titleColor={primary.title_color}
            alignment={primary.alignment}
            title={asText(primary.title)}
            description={isFilled.richText(primary.descriptions) ? <GRichText field={primary.descriptions} /> : null}>
            <Carousel primary={primary} items={validProduct.slice(0, maximumAmount)} lcp={lcp} />
        </ProductsContainer>
    )
}
